import { isNumber, isString, isPlainObject } from 'lodash-es'
import { Node } from './Node'
import * as THREE from '../extras/three'

const MAX_SHADER_LENGTH = 8192

function isValidUniforms(value) {
  if (!isPlainObject(value)) return false
  for (const key in value) {
    const v = value[key]
    if (isNumber(v)) continue
    if (Array.isArray(v) && v.length >= 2 && v.length <= 4 && v.every(isNumber)) continue
    return false
  }
  return true
}

function sameUniformKeys(a, b) {
  if (!a || !b) return false
  const ka = Object.keys(a)
  const kb = Object.keys(b)
  if (ka.length !== kb.length) return false
  for (let i = 0; i < ka.length; i++) {
    if (!(ka[i] in b)) return false
  }
  return true
}

// NOTE: actual defaults bubble up to ClientEnvironment.js
const defaults = {
  bg: null,
  hdr: null,
  shader: null,
  shaderHeader: null,
  shaderUniforms: null,
  rotationY: null,
  sunDirection: null,
  sunIntensity: null,
  sunColor: null,
  fogNear: null,
  fogFar: null,
  fogColor: null,
}

export class Sky extends Node {
  constructor(data = {}) {
    super(data)
    this.name = 'sky'

    this.bg = data.bg
    this.hdr = data.hdr
    this.shader = data.shader
    this.shaderHeader = data.shaderHeader
    this.shaderUniforms = data.shaderUniforms
    this.rotationY = data.rotationY
    this.sunDirection = data.sunDirection
    this.sunIntensity = data.sunIntensity
    this.sunColor = data.sunColor
    this.fogNear = data.fogNear
    this.fogFar = data.fogFar
    this.fogColor = data.fogColor
  }

  mount() {
    this.handle = this.ctx.world.environment.addSky?.(this)
  }

  commit(didMove) {
    if (this.needsRebuild) {
      this.handle?.destroy()
      this.handle = this.ctx.world.environment.addSky?.(this)
      this.needsRebuild = false
    }
  }

  unmount() {
    this.handle?.destroy()
  }

  copy(source, recursive) {
    super.copy(source, recursive)
    this._bg = source._bg
    this._hdr = source._hdr
    this._shader = source._shader
    this._shaderHeader = source._shaderHeader
    this._shaderUniforms = source._shaderUniforms
    this._rotationY = source._rotationY
    this._sunDirection = source._sunDirection
    this._sunIntensity = source._sunIntensity
    this._sunColor = source._sunColor
    this._fogNear = source._fogNear
    this._fogFar = source._fogFar
    this._fogColor = source._fogColor
    return this
  }

  get bg() {
    return this._bg
  }

  set bg(value = defaults.bg) {
    if (value !== null && !isString(value)) {
      throw new Error('[sky] bg not a string')
    }
    if (this._bg === value) return
    this._bg = value
    this.needsRebuild = true
    this.setDirty()
  }

  get hdr() {
    return this._hdr
  }

  set hdr(value = defaults.hdr) {
    if (value !== null && !isString(value)) {
      throw new Error('[sky] hdr not a string')
    }
    if (this._hdr === value) return
    this._hdr = value
    this.needsRebuild = true
    this.setDirty()
  }

  get shader() {
    return this._shader
  }

  set shader(value = defaults.shader) {
    if (value !== null && !isString(value)) {
      throw new Error('[sky] shader not a string')
    }
    if (value !== null && value.length > MAX_SHADER_LENGTH) {
      throw new Error('[sky] shader exceeds max length')
    }
    if (this._shader === value) return
    this._shader = value
    this.needsRebuild = true
    this.setDirty()
  }

  get shaderHeader() {
    return this._shaderHeader
  }

  set shaderHeader(value = defaults.shaderHeader) {
    if (value !== null && !isString(value)) {
      throw new Error('[sky] shaderHeader not a string')
    }
    if (value !== null && value.length > MAX_SHADER_LENGTH) {
      throw new Error('[sky] shaderHeader exceeds max length')
    }
    if (this._shaderHeader === value) return
    this._shaderHeader = value
    this.needsRebuild = true
    this.setDirty()
  }

  get shaderUniforms() {
    return this._shaderUniforms
  }

  set shaderUniforms(value = defaults.shaderUniforms) {
    if (value !== null && !isValidUniforms(value)) {
      throw new Error('[sky] shaderUniforms must be an object of numbers or number arrays (vec2/vec3/vec4)')
    }
    const prev = this._shaderUniforms
    this._shaderUniforms = value
    // Same key set = live-copy values (audio / tempo / uMode). New keys need
    // a rebuild so buildCustomUniformDeclarations actually declares them.
    if (value !== null && sameUniformKeys(prev, value)) {
      this.ctx?.world?.environment?.setShaderUniforms?.(value)
    } else {
      this.needsRebuild = true
    }
    this.setDirty()
  }

  get rotationY() {
    return this._rotationY
  }

  set rotationY(value = defaults.rotationY) {
    if (value !== null && !isNumber(value)) {
      throw new Error('[sky] rotationY not a number')
    }
    if (this._rotationY === value) return
    this._rotationY = value
    this.needsRebuild = true
    this.setDirty()
  }

  get sunDirection() {
    return this._sunDirection
  }

  set sunDirection(value = defaults.sunDirection) {
    if (value !== null && !value?.isVector3) {
      throw new Error('[sky] sunDirection not a Vector3')
    }
    if (this._sunDirection === value) return
    this._sunDirection = value
    // Live-update CSM shadow direction + shader sun without rebuilding the
    // sky material — so an app can rotate the sun every frame cheaply.
    this.ctx?.world?.environment?.setSunDirection?.(value)
    this.setDirty()
  }

  get sunIntensity() {
    return this._sunIntensity
  }

  set sunIntensity(value = defaults.sunIntensity) {
    if (value !== null && !isNumber(value)) {
      throw new Error('[sky] sunIntensity not a number')
    }
    if (this._sunIntensity === value) return
    this._sunIntensity = value
    this.needsRebuild = true
    this.setDirty()
  }

  get sunColor() {
    return this._sunColor
  }

  set sunColor(value = defaults.sunColor) {
    if (value !== null && !isString(value)) {
      throw new Error('[sky] sunColor not a string')
    }
    if (this._sunColor === value) return
    this._sunColor = value
    this.needsRebuild = true
    this.setDirty()
  }

  get fogNear() {
    return this._fogNear
  }

  set fogNear(value = defaults.fogNear) {
    if (value !== null && !isNumber(value)) {
      throw new Error('[sky] fogNear not a number')
    }
    if (this._fogNear === value) return
    this._fogNear = value
    this.needsRebuild = true
    this.setDirty()
  }

  get fogFar() {
    return this._fogFar
  }

  set fogFar(value = defaults.fogFar) {
    if (value !== null && !isNumber(value)) {
      throw new Error('[sky] fogFar not a number')
    }
    if (this._fogFar === value) return
    this._fogFar = value
    this.needsRebuild = true
    this.setDirty()
  }

  get fogColor() {
    return this._fogColor
  }

  set fogColor(value = defaults.fogColor) {
    if (value !== null && !isString(value)) {
      throw new Error('[sky] fogColor not a string')
    }
    if (this._fogColor === value) return
    this._fogColor = value
    this.needsRebuild = true
    this.setDirty()
  }

  getProxy() {
    const self = this
    if (!this.proxy) {
      let proxy = {
        get bg() {
          return self.bg
        },
        set bg(value) {
          self.bg = value
        },
        get hdr() {
          return self.hdr
        },
        set hdr(value) {
          self.hdr = value
        },
        get shader() {
          return self.shader
        },
        set shader(value) {
          self.shader = value
        },
        get shaderHeader() {
          return self.shaderHeader
        },
        set shaderHeader(value) {
          self.shaderHeader = value
        },
        get shaderUniforms() {
          return self.shaderUniforms
        },
        set shaderUniforms(value) {
          self.shaderUniforms = value
        },
        get rotationY() {
          return self.rotationY
        },
        set rotationY(value) {
          self.rotationY = value
        },
        get sunDirection() {
          return self.sunDirection
        },
        set sunDirection(value) {
          self.sunDirection = value
        },
        get sunIntensity() {
          return self.sunIntensity
        },
        set sunIntensity(value) {
          self.sunIntensity = value
        },
        get sunColor() {
          return self.sunColor
        },
        set sunColor(value) {
          self.sunColor = value
        },
        get fogNear() {
          return self.fogNear
        },
        set fogNear(value) {
          self.fogNear = value
        },
        get fogFar() {
          return self.fogFar
        },
        set fogFar(value) {
          self.fogFar = value
        },
        get fogColor() {
          return self.fogColor
        },
        set fogColor(value) {
          self.fogColor = value
        },
      }
      proxy = Object.defineProperties(proxy, Object.getOwnPropertyDescriptors(super.getProxy())) // inherit Node properties
      this.proxy = proxy
    }
    return this.proxy
  }
}